import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import {
  archiveSupplier as archivePersistSupplier,
  deleteSupplierPermanently as deletePersistSupplier,
  createSupplier as persistSupplier,
  listSuppliersResult,
  restoreSupplier as restorePersistSupplier,
  SUPPLIERS_AUTH_REQUIRED_ERRORS,
  updateSupplier as persistSupplierUpdate,
} from '@/modules/suppliers/services/suppliers.service';

import { emptySupplier } from '../supplier-defaults';
export { emptySupplier } from '../supplier-defaults';

function resolveLoadError(result, catalogStatus) {
  if (result.ok) return null;
  if (result.message) return result.message;
  if (catalogStatus === 'module_disabled') return 'Modulo fornitori non abilitato per questo workspace.';
  if (catalogStatus === 'tenant_database_unavailable')
    return 'Database tenant non pronto per il modulo fornitori.';
  if (catalogStatus === 'auth_required') return 'Sessione non autorizzata per il catalogo fornitori.';
  return 'Backend fornitori non disponibile. Elenco fornitori non aggiornato.';
}

export function useSuppliersData({ search = '', status = 'ALL' } = {}) {
  const { logout } = useSession();
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [catalogStatus, setCatalogStatus] = useState('loading');

  const loadSuppliers = useCallback(async () => {
    setIsLoading(true);
    const result = await listSuppliersResult();
    const authRequired = SUPPLIERS_AUTH_REQUIRED_ERRORS.has(result.error);
    const nextCatalogStatus = authRequired
      ? 'auth_required'
      : result.error === 'SUPPLIERS_MODULE_DISABLED'
        ? 'module_disabled'
        : result.error === 'SUPPLIERS_TENANT_DATABASE_UNAVAILABLE'
          ? 'tenant_database_unavailable'
          : !result.ok
            ? 'unavailable'
            : result.data.length > 0
              ? 'available'
              : 'empty';

    if (authRequired) logout();
    setSuppliers(result.data || []);
    setCatalogStatus(nextCatalogStatus);
    setLoadError(resolveLoadError(result, nextCatalogStatus));
    setIsLoading(false);
    return result;
  }, [logout]);

  useEffect(() => {
    let active = true;
    listSuppliersResult().then((result) => {
      if (!active) return;
      const authRequired = SUPPLIERS_AUTH_REQUIRED_ERRORS.has(result.error);
      const nextCatalogStatus = authRequired
        ? 'auth_required'
        : result.error === 'SUPPLIERS_MODULE_DISABLED'
          ? 'module_disabled'
          : result.error === 'SUPPLIERS_TENANT_DATABASE_UNAVAILABLE'
            ? 'tenant_database_unavailable'
            : !result.ok
              ? 'unavailable'
              : result.data.length > 0
                ? 'available'
                : 'empty';
      if (authRequired) logout();
      setSuppliers(result.data || []);
      setCatalogStatus(nextCatalogStatus);
      setLoadError(resolveLoadError(result, nextCatalogStatus));
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [logout]);

  const filteredSuppliers = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();
    return suppliers.filter((supplier) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          supplier.displayName,
          supplier.legalName,
          supplier.category,
          supplier.contactName,
          supplier.contactEmail,
          supplier.vatNumber,
        ].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(normalizedSearch)
        );
      const matchesStatus = status === 'ALL' || supplier.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [search, status, suppliers]);

  const counters = useMemo(
    () => ({
      active: suppliers.filter((supplier) => supplier.status === 'ACTIVE').length,
      inactive: suppliers.filter((supplier) => supplier.status === 'INACTIVE').length,
    }),
    [suppliers]
  );

  const createSupplier = async (payload) => {
    const result = await persistSupplier(payload);
    if (!result.ok) return result;
    setSuppliers((prev) =>
      [...prev, result.data.supplier].sort((left, right) => left.displayName.localeCompare(right.displayName))
    );
    return result;
  };

  const updateSupplier = async (payload) => {
    const result = await persistSupplierUpdate(payload.id, payload);
    if (!result.ok) return result;
    setSuppliers((prev) =>
      prev.map((supplier) => (supplier.id === result.data.supplier.id ? result.data.supplier : supplier))
    );
    return result;
  };

  const archiveSupplier = async (supplierId) => {
    const result = await archivePersistSupplier(supplierId);
    if (!result.ok) return result;
    setSuppliers((prev) =>
      prev.map((supplier) =>
        String(supplier.id) === String(supplierId) ? { ...supplier, status: 'INACTIVE' } : supplier
      )
    );
    return result;
  };

  const restoreSupplier = async (supplierId) => {
    const result = await restorePersistSupplier(supplierId);
    if (!result.ok) return result;
    setSuppliers((prev) =>
      prev.map((supplier) =>
        String(supplier.id) === String(result.data.supplier.id) ? result.data.supplier : supplier
      )
    );
    return result;
  };

  const deleteSupplierPermanently = async (supplierId) => {
    const result = await deletePersistSupplier(supplierId);
    if (result.ok)
      setSuppliers((prev) => prev.filter((supplier) => String(supplier.id) !== String(supplierId)));
    return result;
  };

  return {
    suppliers,
    filteredSuppliers,
    counters,
    catalogStatus,
    isLoading,
    loadError,
    emptySupplier,
    loadSuppliers,
    createSupplier,
    updateSupplier,
    archiveSupplier,
    restoreSupplier,
    deleteSupplierPermanently,
  };
}
